import { MgmtApi } from '@/customApiClients/mgmtApiClient/Api';
import { cn } from '@/lib/utils';
import {
    FileArchive,
    File as FileIcon,
    FileText,
    Image as ImageIcon,
    Loader2,
    Trash2,
    UploadCloud,
    Video
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/ui/section-header';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/ui/table-skeleton';

interface ReferenceFileRow {
    rowId: string; // mgmt ClaudeFile row guid
    anthropicFileId: string;
    filename: string;
    organization?: string | null;
}

interface ReferenceFilesTabProps {
    mgmtApi: MgmtApi<unknown>;
}

// Client-side-only guard — the backend has no hard size limit, this just
// keeps the upload area from silently hanging on something huge.
const MAX_FILE_SIZE_MB = 30;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

type StagedFileStatus = 'uploading' | 'error';

interface StagedFile {
    id: string;
    file: File;
    status: StagedFileStatus;
    error?: string;
}

const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Shared by the staged-upload rows (which know the file's mime type) and the
// already-uploaded rows (filename only, no mime type from the API).
const FileTypeIcon: React.FC<{ name: string; mimeType?: string }> = ({
    name,
    mimeType
}) => {
    if (
        mimeType?.startsWith('image/') ||
        /\.(png|jpe?g|gif|webp|svg)$/i.test(name)
    )
        return <ImageIcon className="size-4" />;
    if (mimeType?.startsWith('video/') || /\.(mp4|mov|avi|webm)$/i.test(name))
        return <Video className="size-4" />;
    if (mimeType === 'application/zip' || /\.(zip|tar|gz|rar)$/i.test(name))
        return <FileArchive className="size-4" />;
    if (
        mimeType === 'application/pdf' ||
        mimeType?.startsWith('text/') ||
        /\.(pdf|docx?|md|csv|json)$/i.test(name)
    )
        return <FileText className="size-4" />;
    return <FileIcon className="size-4" />;
};

export const ReferenceFilesTab: React.FC<ReferenceFilesTabProps> = ({
    mgmtApi
}) => {
    const [files, setFiles] = useState<ReferenceFileRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
    const nextStagedId = useRef(0);

    const [deleteTarget, setDeleteTarget] = useState<ReferenceFileRow | null>(
        null
    );

    // `silent` skips the loading-spinner flip — used when refreshing after an
    // upload/delete, where the list is already on screen and flipping to the
    // spinner and back just flickers the whole section for no reason.
    const fetchFiles = (opts: { silent?: boolean } = {}) => {
        if (!opts.silent) setLoading(true);
        fetch('/mgmt/api/v1/ClaudeFiles', { credentials: 'include' })
            .then((res) => (res.ok ? res.json() : []))
            .then((data) => {
                const rows: ReferenceFileRow[] = (
                    Array.isArray(data) ? data : []
                ).map((f: any) => ({
                    rowId: f.id,
                    anthropicFileId: f.anthropicFileId,
                    filename: f.filename,
                    organization: f.organization
                }));
                setFiles(rows);
            })
            .catch(() => {
                if (!opts.silent) setFiles([]);
                toast.error('Failed to load reference files');
            })
            .finally(() => {
                if (!opts.silent) setLoading(false);
            });
    };

    useEffect(() => fetchFiles(), []);

    // Uploads one already-selected file: raw bytes to Anthropic's Files API,
    // then a thin org-scoped pointer row in mgmt-API so listing stays a fast
    // DB query instead of leaking cross-org.
    const uploadSingleFile = async (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        const uploadRes = await fetch('/api/reference-files', {
            method: 'POST',
            credentials: 'include',
            body: formData
        });
        if (!uploadRes.ok) throw new Error('Failed to upload file');
        const uploaded: { id: string; filename: string } =
            await uploadRes.json();

        const created = await fetch('/mgmt/api/v1/ClaudeFiles', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                anthropicFileId: uploaded.id,
                filename: uploaded.filename
            })
        }).then((res) => (res.ok ? res.json() : null));
        if (!created) throw new Error('Failed to register file');
    };

    // Uploads a just-staged file right away and reconciles its row: removed
    // on success (it's now in the real table below), left in place with an
    // error message on failure so it can be dismissed or inspected.
    const uploadStagedFile = (entry: StagedFile) => {
        uploadSingleFile(entry.file)
            .then(() => {
                setStagedFiles((prev) => prev.filter((f) => f.id !== entry.id));
                fetchFiles({ silent: true });
                toast.success('Reference file uploaded!');
            })
            .catch((e: any) => {
                setStagedFiles((prev) =>
                    prev.map((f) =>
                        f.id === entry.id
                            ? {
                                  ...f,
                                  status: 'error',
                                  error:
                                      e?.message ||
                                      'Failed to upload reference file'
                              }
                            : f
                    )
                );
            });
    };

    // Stages every dropped file and immediately kicks off its upload — no
    // confirm step. Rejections (currently just oversize) become dismissable
    // error rows instead of silently dropping them.
    const onDrop = useCallback(
        (accepted: File[], rejections: FileRejection[]) => {
            const acceptedEntries: StagedFile[] = accepted.map((file) => ({
                id: `f-${nextStagedId.current++}`,
                file,
                status: 'uploading'
            }));
            const rejectedEntries: StagedFile[] = rejections.map((r) => ({
                id: `f-${nextStagedId.current++}`,
                file: r.file,
                status: 'error',
                error:
                    r.errors[0]?.code === 'file-too-large'
                        ? `File exceeds ${MAX_FILE_SIZE_MB}MB limit`
                        : r.errors[0]?.message || 'File rejected'
            }));
            setStagedFiles((prev) => [
                ...prev,
                ...acceptedEntries,
                ...rejectedEntries
            ]);
            acceptedEntries.forEach(uploadStagedFile);
        },
        []
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: true,
        maxSize: MAX_FILE_SIZE_BYTES
    });

    const removeStagedFile = (id: string) =>
        setStagedFiles((prev) => prev.filter((f) => f.id !== id));

    const handleDelete = async (file: ReferenceFileRow) => {
        setDeleteTarget(null);
        setDeletingId(file.rowId);
        try {
            const res = await fetch(
                `/api/reference-files/${file.anthropicFileId}`,
                { method: 'DELETE', credentials: 'include' }
            );
            if (!res.ok) throw new Error('Failed to delete reference file');

            const mgmtRes = await fetch(
                `/mgmt/api/v1/ClaudeFiles/${file.rowId}`,
                { method: 'DELETE', credentials: 'include' }
            );
            if (!mgmtRes.ok) {
                const j = await mgmtRes.json().catch(() => null);
                throw new Error(
                    j?.detail ||
                        'File was deleted on Anthropic, but removing it from the list failed — it may still be referenced by a Deep Intelligence schedule.'
                );
            }

            setFiles((prev) => prev.filter((f) => f.rowId !== file.rowId));
            toast.success('Reference file deleted.');
        } catch (e: any) {
            toast.error(e?.message || 'Failed to delete reference file');
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="space-y-8">
            <section className="space-y-6">
                <SectionHeader
                    title="Reference Files"
                    description="Read-only files mounted into the sandbox on every scheduled run (e.g. a template or fixed dataset)."
                />

                {loading && <TableSkeleton columns={3} />}

                {!loading && (
                    <div
                        {...getRootProps()}
                        className={cn(
                            'flex flex-col justify-center items-center gap-3 bg-muted p-8 border-2 border-dashed rounded-xl text-center transition-colors cursor-pointer',
                            isDragActive
                                ? 'border-foreground bg-foreground/5'
                                : 'bg-muted/5 dark:bg-muted/10 border-muted-foreground/20 dark:border-muted-foreground/10 hover:border-foreground/40'
                        )}
                    >
                        <input {...getInputProps()} />
                        <div className="flex justify-center items-center bg-muted border border-border rounded-lg size-12 text-foreground">
                            <UploadCloud className="size-6" />
                        </div>
                        <div className="flex flex-col items-center gap-0.5">
                            <p className="font-medium text-foreground text-sm">
                                Click to{' '}
                                <span className="text-foreground underline underline-offset-2">
                                    upload
                                </span>{' '}
                                or drag and drop
                            </p>
                            <p className="text-muted-foreground text-xs">
                                max: {MAX_FILE_SIZE_MB}MB per file
                            </p>
                        </div>
                    </div>
                )}

                {!loading && stagedFiles.length > 0 && (
                    <div className="space-y-3">
                        <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-hidden">
                            {stagedFiles.map((sf) => (
                                <div
                                    key={sf.id}
                                    className="flex items-center gap-3 p-3 border border-border rounded-lg"
                                >
                                    <div
                                        className={cn(
                                            'flex justify-center items-center rounded-md size-9 shrink-0',
                                            sf.status === 'error'
                                                ? 'bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-500'
                                                : 'bg-muted text-muted-foreground'
                                        )}
                                    >
                                        <FileTypeIcon
                                            name={sf.file.name}
                                            mimeType={sf.file.type}
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-sm truncate">
                                            {sf.file.name}
                                        </div>
                                        <div className="text-xs">
                                            {sf.status === 'error' ? (
                                                <span className="text-red-600 dark:text-red-500">
                                                    {sf.error}
                                                </span>
                                            ) : (
                                                <span className="text-muted-foreground">
                                                    {formatBytes(sf.file.size)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="shrink-0">
                                        {sf.status === 'uploading' && (
                                            <Loader2 className="m-2 size-4 text-muted-foreground animate-spin" />
                                        )}
                                        {sf.status === 'error' && (
                                            <Button
                                                variant="ghost"
                                                size="icon-sm"
                                               
                                                onClick={() =>
                                                    removeStagedFile(sf.id)
                                                }
                                            >
                                                <Trash2 />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {!loading && files.length > 0 && (
                    <div>
                        <Table variant="fluid">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Filename</TableHead>
                                    <TableHead className="text-sm">
                                        Anthropic file ID
                                    </TableHead>
                                    <TableHead className="text-right">
                                        {/* Actions */}
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {files.map((file) => (
                                    <TableRow key={file.rowId}>
                                        <TableCell className="font-medium">
                                            <div className="flex items-center gap-2.5">
                                                <div className="flex justify-center items-center bg-muted rounded-md size-8 text-muted-foreground shrink-0">
                                                    <FileTypeIcon
                                                        name={file.filename}
                                                    />
                                                </div>
                                                {file.filename}
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-mono text-muted-foreground text-sm">
                                            {file.anthropicFileId}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                variant="destructive"
                                                size="default"
                                                onClick={() =>
                                                    setDeleteTarget(file)
                                                }
                                                disabled={
                                                    deletingId === file.rowId
                                                }
                                                className="text-sm"
                                            >
                                                {deletingId === file.rowId ? (
                                                    <Loader2 className="animate-spin" />
                                                ) : (
                                                    <Trash2 />
                                                )}
                                                Delete
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </section>

            <AlertDialog
                open={!!deleteTarget}
                onOpenChange={(open) => !open && setDeleteTarget(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Delete "{deleteTarget?.filename}"?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            This removes the file from Anthropic and from this
                            list. This cannot be undone, and it may still be
                            referenced by a Deep Intelligence schedule.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction variant="destructive" onClick={() =>
                                    deleteTarget && handleDelete(deleteTarget)
                                }>
                                Delete permanently
                            </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};
